import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import "./ui.css";

export type ToastTone = "info" | "success" | "warning" | "danger";

export interface ToastItem {
  id: string;
  tone: ToastTone;
  title: string;
  body?: ReactNode;
  durationMs?: number;
}

interface ToastContextValue {
  push: (toast: Omit<ToastItem, "id">) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (toast: Omit<ToastItem, "id">) => {
      counter.current += 1;
      const id = `t-${Date.now()}-${counter.current}`;
      const item: ToastItem = { id, durationMs: 5000, ...toast };
      setItems((prev) => [...prev, item]);
      if (item.durationMs && item.durationMs > 0) {
        window.setTimeout(() => dismiss(id), item.durationMs);
      }
      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="toast-stack" role="region" aria-label="Notifications">
          {items.map((toast) => (
            <ToastView key={toast.id} toast={toast} onClose={() => dismiss(toast.id)} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

function ToastView({ toast, onClose }: { toast: ToastItem; onClose: () => void }) {
  return (
    <div className={`toast toast--${toast.tone}`} role="status">
      <div className="toast__body">
        <div className="toast__title">{toast.title}</div>
        {toast.body && <div className="toast__sub">{toast.body}</div>}
      </div>
      <button
        type="button"
        className="toast__close"
        aria-label="Dismiss"
        onClick={onClose}
      >
        ×
      </button>
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    return {
      push: () => "",
      dismiss: () => undefined,
    };
  }
  return ctx;
}

/**
 * Auto-dismiss helper: pushes a toast and resolves when it disappears.
 */
export function useToastEffect(toast: ToastItem | null) {
  const { push, dismiss } = useToast();
  useEffect(() => {
    if (!toast) return;
    const id = push(toast);
    return () => dismiss(id);
  }, [toast, push, dismiss]);
}
